// src/pages/Public/Services.jsx
import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import {
    AirExpressIcon,
    SeaEconomyIcon,
    CustomsIcon,
    GroupageIcon,
    StoreDetailedIcon,
    PremiumIcon,
    PresenceIcon,
    SupportIcon,
    RealTimeIcon,
    SpeedIcon,
    PriceIcon,
    SecurityIcon
} from '../../components/Icons/ServiceIcons';

const Services = () => {
    const [animated, setAnimated] = useState(false);
    const [whatsappNumber, setWhatsappNumber] = useState('');

    useEffect(() => {
        setAnimated(true);
        fetchWhatsappNumber();
    }, []);

    const fetchWhatsappNumber = async () => {
        try {
            const response = await axios.get('https://chineafriquecargo.com/whatsapp/public/');
            let numbers = [];
            if (response.data && response.data.results) {
                numbers = response.data.results;
            } else if (Array.isArray(response.data)) {
                numbers = response.data;
            }
            const defaultNum = numbers.find(n => n.is_default) || numbers[0];
            if (defaultNum) {
                setWhatsappNumber(defaultNum.phone_number);
            } else {
                setWhatsappNumber('+237 612345678');
            }
        } catch (err) {
            console.error('Erreur chargement WhatsApp:', err);
            setWhatsappNumber('+237 612345678');
        }
    };

    const cleanPhoneNumber = (phone) => {
        return phone.replace(/\s+/g, '').replace(/[^0-9+]/g, '');
    };

    const services = [
        {
            id: 1,
            title: 'Transport Aérien Express',
            icon: <AirExpressIcon />,
            description: 'Solution rapide pour vos marchandises urgentes. Idéal pour les petits volumes et produits de valeur.',
            features: [
                'Délai: 5-10 jours',
                'Suivi en temps réel',
                'Assurance optionnelle',
                'Livraison door-to-door'
            ],
            color: '#1565C0',
            delay: 0
        },
        {
            id: 2,
            title: 'Transport Maritime Économique',
            icon: <SeaEconomyIcon />,
            description: 'Solution économique pour gros volumes. Parfait pour les importateurs et commerçants.',
            features: [
                'Délai: 45-90 jours',
                'Groupage (LCL) disponible',
                'Conteneur complet (FCL)',
                'Tarifs dégressifs'
            ],
            color: '#2E7D32',
            delay: 100
        },
        {
            id: 3,
            title: 'Dédouanement',
            icon: <CustomsIcon />,
            description: 'Prise en charge complète de vos formalités douanières en Chine et en Afrique.',
            features: [
                'Déclaration en douane',
                'Gestion des taxes',
                "Certificats d'origine",
                'Expertise locale'
            ],

            color: '#F9A825',
            delay: 200
        },
        {
            id: 4,
            title: 'Groupeur (LCL)',
            icon: <GroupageIcon />,
            description: 'Partagez votre conteneur avec d\'autres marchandises pour réduire vos coûts.',
            features: [
                "Jusqu'à 60% d'économies",
                'Flexibilité totale',
                'Départ fréquent',
                'Idéal pour petits commerçants'
            ],
            color: '#C62828',
            delay: 300
        },
        {
            id: 5,
            title: 'CAC Store',
            icon: <StoreDetailedIcon />,
            description: 'Commandez des produits directement depuis la Chine. Nous gérons tout pour vous.',
            features: [
                'Catalogue varié',
                'Achat chez fournisseurs',
                'Contrôle qualité',
                'Livraison incluse'
            ],
            price: 'Prix produits + transport',
            color: '#6A1B9A',
            delay: 400
        },
        {
            id: 6,
            title: 'Service Premium',
            icon: <PremiumIcon />,
            description: 'Solution sur-mesure pour les entreprises avec des besoins spécifiques.',
            features: [
                'Account manager dédié',
                'Priorité à toutes les étapes',
                'Assurance premium',
                'Support 24/7'
            ],
            price: 'Sur devis personnalisé',
            color: '#E65100',
            delay: 500
        }
    ];

    const advantages = [
        { icon: <PresenceIcon />, title: 'Présence Chine-Afrique', desc: 'Des bureaux en Chine et dans 5 pays africains pour un suivi local', color: '#1565C0' },
        { icon: <SupportIcon />, title: 'Support Bilingue', desc: 'Équipe francophone et chinoise pour vous accompagner', color: '#2E7D32' },
        { icon: <RealTimeIcon />, title: 'Suivi en Temps Réel', desc: 'Plateforme de tracking pour suivre votre colis à chaque étape', color: '#C62828' },
        { icon: <SpeedIcon />, title: 'Rapidité', desc: 'Livraison express en 5-10 jours pour le transport aérien', color: '#F9A825' },
        { icon: <PriceIcon />, title: 'Tarifs Compétitifs', desc: 'Les meilleurs prix du marché avec une qualité de service optimale', color: '#6A1B9A' },
        { icon: <SecurityIcon />, title: 'Sécurité', desc: 'Assurance optionnelle et suivi sécurisé de vos marchandises', color: '#E65100' }
    ];

    return (
        <>
            <Helmet>
                <title>CAC - Nos Services | Transport Chine Afrique</title>
                <meta name="description" content="Découvrez nos services de transport entre la Chine et l'Afrique : fret aérien, fret maritime, dédouanement, groupage, CAC Store et service premium. Solutions logistiques adaptées à vos besoins." />
                <meta name="keywords" content="transport aérien, transport maritime, dédouanement, groupage, CAC Store, logistique chine afrique, fret, import export" />
            </Helmet>

            {/* Hero Section Moderne */}
            <div className="services-hero">
                <div className="hero-overlay"></div>
                <Container className="hero-container">
                    <div className={`hero-content ${animated ? 'fade-in-up' : ''}`}>
                        <div className="hero-badge">
                            <span className="badge-pulse"> Solutions Expert</span>
                        </div>
                        <h1 className="hero-title">
                            Nos <span className="text-gradient">Services</span>
                        </h1>
                        <p className="hero-subtitle">
                            Des solutions logistiques adaptées à tous vos besoins
                        </p>
                    </div>
                </Container>
                <div className="hero-wave">
                    <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0 64L60 69.3C120 75 240 85 360 80C480 75 600 53 720 48C840 43 960 53 1080 58.7C1200 64 1320 64 1380 64L1440 64L1440 120L1380 120C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120L0 120Z" fill="#f8f9fa" />
                    </svg>
                </div>
            </div>

            {/* Services Grid */}
            <div className="services-section">
                <Container>
                    <div className="section-header">
                        <h2 className="section-title">Découvrez <span className="text-gradient">nos solutions</span></h2>
                        <p className="section-subtitle">Une gamme complète de services pour répondre à tous vos besoins logistiques</p>
                    </div>
                    <Row className="g-4">
                        {services.map((service) => (
                            <Col lg={4} md={6} key={service.id}>
                                <div className="service-card" style={{ animationDelay: `${service.delay}ms` }}>
                                    <div className="service-icon" style={{ backgroundColor: `${service.color}15`, color: service.color }}>
                                        {service.icon}
                                    </div>
                                    <h3 className="service-title">{service.title}</h3>
                                    <p className="service-description">{service.description}</p>
                                    <div className="service-features">
                                        {service.features.map((feature, idx) => (
                                            <div key={idx} className="feature-item">
                                                <span className="feature-check">✓</span>
                                                <span>{feature}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="service-footer">
                                        <div className="service-price">{service.price}</div>
                                        <Button as={Link} to="/contact" className="service-btn">
                                            Demander un devis →
                                        </Button>
                                    </div>
                                    <div className="service-hover-line" style={{ backgroundColor: service.color }}></div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </div>

            {/* Why Choose Us - Version Moderne */}
            <div className="advantages-section">
                <Container>
                    <div className="section-header">
                        <h2 className="section-title">Pourquoi <span className="text-gradient">choisir CAC</span> ?</h2>
                        <p className="section-subtitle">Notre engagement pour vous garantir une expérience logistique sans faille</p>
                    </div>
                    <Row className="g-4">
                        {advantages.map((adv, idx) => (
                            <Col md={6} lg={4} key={idx}>
                                <div className="advantage-card">
                                    <div className="advantage-icon" style={{ backgroundColor: `${adv.color}15`, color: adv.color }}>
                                        {adv.icon}
                                    </div>
                                    <h4 className="advantage-title">{adv.title}</h4>
                                    <p className="advantage-desc">{adv.desc}</p>
                                    <div className="advantage-line" style={{ backgroundColor: adv.color }}></div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </div>

            {/* Stats Section */}
            <div className="stats-section">
                <Container>
                    <Row className="g-4">
                        <Col md={3} sm={6}>
                            <div className="stat-card">
                                <div className="stat-number">10+</div>
                                <div className="stat-label">Années d'expérience</div>
                            </div>
                        </Col>
                        <Col md={3} sm={6}>
                            <div className="stat-card">
                                <div className="stat-number">5000+</div>
                                <div className="stat-label">Colis transportés</div>
                            </div>
                        </Col>
                        <Col md={3} sm={6}>
                            <div className="stat-card">
                                <div className="stat-number">5</div>
                                <div className="stat-label">Pays desservis</div>
                            </div>
                        </Col>
                        <Col md={3} sm={6}>
                            <div className="stat-card">
                                <div className="stat-number">98%</div>
                                <div className="stat-label">Satisfaction client</div>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>

            {/* CTA Section */}
            <div className="cta-section">
                <Container className="text-center">
                    <h2 className="cta-title">Prêt à expédier ?</h2>
                    <p className="cta-subtitle">
                        Contactez-nous dès maintenant pour un devis gratuit et personnalisé
                    </p>
                    <div className="cta-buttons">
                        <Button as={Link} to="/contact" className="btn-cta-primary">
                            📞 Nous contacter
                        </Button>
                        <Button
                            as="a"
                            href={`https://wa.me/${cleanPhoneNumber(whatsappNumber)}`}
                            target="_blank"
                            className="btn-cta-whatsapp"
                        >
                            💬 WhatsApp
                        </Button>
                    </div>
                </Container>
            </div>

            <style>{`
                /* Hero Section */
                .services-hero {
                    position: relative;
                    background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
                    min-height: 400px;
                    display: flex;
                    align-items: center;
                    overflow: hidden;
                }
                
                .hero-overlay {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: radial-gradient(circle at 20% 50%, rgba(198, 40, 40, 0.2) 0%, transparent 50%);
                }
                
                .hero-container {
                    position: relative;
                    z-index: 2;
                    padding: 80px 0;
                }
                
                .hero-content {
                    text-align: center;
                    max-width: 700px;
                    margin: 0 auto;
                }
                
                .hero-badge {
                    margin-bottom: 24px;
                }
                
                .badge-pulse {
                    display: inline-block;
                    padding: 8px 20px;
                    background: rgba(249, 168, 37, 0.15);
                    border: 1px solid rgba(249, 168, 37, 0.3);
                    border-radius: 50px;
                    font-size: 14px;
                    font-weight: 600;
                    color: #F9A825;
                    animation: pulse 2s infinite;
                }
                
                @keyframes pulse {
                    0% { box-shadow: 0 0 0 0 rgba(249, 168, 37, 0.4); }
                    70% { box-shadow: 0 0 0 10px rgba(249, 168, 37, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(249, 168, 37, 0); }
                }
                
                .hero-title {
                    font-size: 56px;
                    font-weight: 800;
                    margin-bottom: 20px;
                    color: white;
                }
                
                .text-gradient {
                    background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                }
                
                .hero-subtitle {
                    font-size: 18px;
                    color: rgba(255, 255, 255, 0.7);
                }
                
                .hero-wave {
                    position: absolute;
                    bottom: -1px;
                    left: 0;
                    right: 0;
                }
                
                /* Services Section */
                .services-section {
                    padding: 80px 0;
                    background: #f8f9fa;
                }
                
                .section-header {
                    text-align: center;
                    margin-bottom: 50px;
                }
                
                .section-title {
                    font-size: 40px;
                    font-weight: 800;
                    margin-bottom: 16px;
                }
                
                .section-subtitle {
                    font-size: 18px;
                    color: #666;
                    max-width: 600px;
                    margin: 0 auto;
                }
                
                .service-card {
                    background: white;
                    border-radius: 24px;
                    padding: 30px;
                    position: relative;
                    overflow: hidden;
                    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                    height: 100%;
                    animation: fadeInUp 0.6s ease forwards;
                    opacity: 0;
                    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
                }
                
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                .service-card:hover {
                    transform: translateY(-10px);
                    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
                }
                
                .service-icon {
                    width: 80px;
                    height: 80px;
                    border-radius: 20px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-bottom: 20px;
                    transition: transform 0.3s ease;
                }
                
                .service-card:hover .service-icon svg {
                    transform: scale(1.1);
                }
                
                .service-title {
                    font-size: 22px;
                    font-weight: 700;
                    margin-bottom: 12px;
                }
                
                .service-description {
                    color: #666;
                    font-size: 14px;
                    line-height: 1.6;
                    margin-bottom: 20px;
                }
                
                .service-features {
                    margin-bottom: 24px;
                }
                
                .feature-item {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 8px 0;
                    font-size: 14px;
                    color: #555;
                }
                
                .feature-check {
                    color: #C62828;
                    font-weight: bold;
                    font-size: 16px;
                }
                
                .service-footer {
                    border-top: 1px solid #eee;
                    padding-top: 20px;
                    text-align: center;
                }
                
                .service-price {
                    font-size: 18px;
                    font-weight: bold;
                    color: #C62828;
                    margin-bottom: 16px;
                }
                
                .service-btn {
                    background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
                    border: none;
                    padding: 10px 24px;
                    border-radius: 50px;
                    font-size: 14px;
                    font-weight: 600;
                    width: 100%;
                    transition: all 0.3s ease;
                }
                
                .service-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 5px 15px rgba(198, 40, 40, 0.4);
                }
                
                .service-hover-line {
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    height: 4px;
                    transform: scaleX(0);
                    transition: transform 0.3s ease;
                }
                
                .service-card:hover .service-hover-line {
                    transform: scaleX(1);
                }
                
                /* Advantages Section */
                .advantages-section {
                    padding: 80px 0;
                    background: white;
                }
                
                .advantage-card {
                    text-align: center;
                    padding: 30px;
                    background: #f8f9fa;
                    border-radius: 20px;
                    transition: all 0.3s ease;
                    position: relative;
                    overflow: hidden;
                }
                
                .advantage-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
                }
                
                .advantage-icon {
                    width: 70px;
                    height: 70px;
                    border-radius: 20px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 20px;
                    transition: transform 0.3s ease;
                }
                
                .advantage-card:hover .advantage-icon svg {
                    transform: scale(1.1);
                }
                
                .advantage-title {
                    font-size: 18px;
                    font-weight: 700;
                    margin-bottom: 12px;
                }
                
                .advantage-desc {
                    font-size: 14px;
                    color: #666;
                    line-height: 1.6;
                }
                
                .advantage-line {
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    height: 3px;
                    transform: scaleX(0);
                    transition: transform 0.3s ease;
                }
                
                .advantage-card:hover .advantage-line {
                    transform: scaleX(1);
                }
                
                /* Stats Section */
                .stats-section {
                    padding: 60px 0;
                    background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
                    color: white;
                }
                
                .stat-card {
                    text-align: center;
                    padding: 30px;
                    background: rgba(255, 255, 255, 0.05);
                    border-radius: 20px;
                    backdrop-filter: blur(10px);
                    transition: all 0.3s ease;
                }
                
                .stat-card:hover {
                    transform: translateY(-5px);
                    background: rgba(255, 255, 255, 0.1);
                }
                
                .stat-number {
                    font-size: 48px;
                    font-weight: 800;
                    background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                    margin-bottom: 8px;
                }
                
                .stat-label {
                    font-size: 16px;
                    color: rgba(255, 255, 255, 0.7);
                }
                
                /* CTA Section */
                .cta-section {
                    padding: 80px 0;
                    background: #f8f9fa;
                }
                
                .cta-title {
                    font-size: 40px;
                    font-weight: 800;
                    margin-bottom: 16px;
                }
                
                .cta-subtitle {
                    font-size: 18px;
                    color: #666;
                    margin-bottom: 32px;
                }
                
                .cta-buttons {
                    display: flex;
                    gap: 16px;
                    justify-content: center;
                    flex-wrap: wrap;
                }
                
                .btn-cta-primary {
                    background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
                    border: none;
                    padding: 14px 32px;
                    border-radius: 50px;
                    font-weight: 600;
                    transition: all 0.3s ease;
                    color: white;
                }
                
                .btn-cta-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 25px rgba(198, 40, 40, 0.4);
                }
                
                .btn-cta-whatsapp {
                    background: #25D366;
                    border: none;
                    padding: 14px 32px;
                    border-radius: 50px;
                    font-weight: 600;
                    transition: all 0.3s ease;
                    color: white;
                }
                
                .btn-cta-whatsapp:hover {
                    transform: translateY(-2px);
                    background: #128C7E;
                }
                
                /* Animations */
                .fade-in-up {
                    animation: fadeInUp 0.8s ease forwards;
                }
                
                /* Responsive */
                @media (max-width: 992px) {
                    .hero-title {
                        font-size: 40px;
                    }
                    
                    .section-title {
                        font-size: 32px;
                    }
                    
                    .cta-title {
                        font-size: 32px;
                    }
                    
                    .service-icon {
                        width: 60px;
                        height: 60px;
                    }
                    
                    .advantage-icon {
                        width: 55px;
                        height: 55px;
                    }
                }
                
                @media (max-width: 768px) {
                    .hero-title {
                        font-size: 32px;
                    }
                    
                    .section-title {
                        font-size: 28px;
                    }
                    
                    .service-card {
                        padding: 20px;
                    }
                    
                    .stat-number {
                        font-size: 36px;
                    }
                    
                    .service-icon {
                        width: 50px;
                        height: 50px;
                    }
                    
                    .service-icon svg {
                        width: 30px;
                        height: 30px;
                    }
                    
                    .advantage-icon {
                        width: 45px;
                        height: 45px;
                    }
                    
                    .advantage-icon svg {
                        width: 25px;
                        height: 25px;
                    }
                }
            `}</style>
        </>
    );
};

export default Services;